import { useCallback, useId, useState } from 'react'

import {
  type ChosenTimetable,
  type TimetableElement,
  type TimetablePlan,
  chooseTimetable,
  timetablePlan,
  whoseTimetable,
} from '../../api/connections'
import { asFailure, type Failure } from '../../api/request'
import { useLoad } from '../../api/useLoad'
import { Button } from '../../ui/Control'
import { Done, Failed, Waiting } from '../../ui/Status'
import { Switch } from '../../ui/Switch'
import { ClassPicker } from './ClassPicker'

/**
 * What is taken over from a system that brings a **timetable**, in place of the folders.
 *
 * Everything here is asked once on the form that sets the connection up — this panel exists to
 * *change* it afterwards, not to complete it. What is chosen is whose plan, from which day, and
 * whether a double period is one appointment or two, and the three are saved together because they
 * are read together on every run.
 *
 * Three things about it are decisions rather than layout.
 *
 * **Whose plan is asked over there, not guessed here.** Some accounts *are* a person and simply have
 * a plan; a shared university login is nobody in particular, and then a class has to be chosen. The
 * screen shows one sentence or a list depending on what the other system answered, so nobody has to
 * know which kind of account they were given. That answer costs a sign-in over there, so it is asked
 * for by a control rather than fetched because a panel became visible.
 *
 * **There is one date and it is the beginning.** Nobody is asked how far into the future their own
 * timetable goes — the source says how far its plan reaches and is asked afresh on every run, so
 * everything from that day on comes over and nothing quietly stops arriving in November.
 *
 * **The merging says what it costs before it is changed.** Switching it makes new appointments once:
 * the ones taken over under the old setting keep their ids, are marked as no longer offered, and keep
 * everything hanging off them. That is a real cost and it is said here rather than discovered later.
 */
export interface TimetableSourceProps {
  readonly connectionId: string
  /** What the system is called, for the sentences. `WebUntis`. */
  readonly systemName: string
  /** False when the kept password cannot be read — then there is nothing to ask over there. */
  readonly usable: boolean
}

export function TimetableSource({ connectionId, systemName, usable }: TimetableSourceProps) {
  const plan = useLoad(
    useCallback((signal: AbortSignal) => timetablePlan(connectionId, signal), [connectionId]),
  )

  const known = plan.load.status === 'ready' ? plan.load.value : null

  if (!usable) {
    return (
      <section className="source source--timetable">
        <h4 className="source__title">What comes over</h4>
        <p className="source__lead">
          MyLib cannot read the password kept for this connection, so it cannot ask {systemName}{' '}
          whose plan it may fetch. Enter the password again and this comes back as it was.
        </p>
      </section>
    )
  }

  return (
    <section className="source source--timetable">
      <h4 className="source__title">What comes over</h4>
      <p className="source__lead">
        {systemName} brings a timetable rather than folders. Everything from the day below is taken
        over and kept up to date, as far ahead as {systemName} publishes it.
      </p>

      {plan.load.status === 'waiting' && <Waiting label="Looking up what is chosen." />}
      {plan.load.status === 'failed' && (
        <Failed failure={plan.load.failure} onRetry={plan.retry} title="That did not load" />
      )}

      {known !== null && (
        <ChosenPlan
          /*
            Keyed by what the service last said, which is what seeds the fields.

            The values belong to whoever is typing them until they are saved, so nothing may write
            over them while that is happening — and a save is a new truth, so the panel comes back as
            a new panel with the new values in it. The same rule the appointment sheet runs on, and
            the reason there is no effect in this file writing state.
          */
          key={known.window?.chosenAt ?? 'nothing-chosen'}
          connectionId={connectionId}
          systemName={systemName}
          plan={known}
          onSaved={plan.retry}
        />
      )}
    </section>
  )
}

/**
 * The three choices, seeded once from what is stored and owned by whoever is typing afterwards.
 *
 * A component of its own so that **mounting is the seeding**: there is no effect anywhere here
 * writing state, and therefore no moment in which the screen shows a value nobody chose.
 */
function ChosenPlan({
  connectionId,
  systemName,
  plan,
  onSaved,
}: {
  readonly connectionId: string
  readonly systemName: string
  readonly plan: TimetablePlan
  readonly onSaved: () => void
}) {
  const base = useId()
  const chosen = plan.window

  const [from, setFrom] = useState(chosen?.from ?? plan.suggestedFrom)
  const [element, setElement] = useState<TimetableElement | null>(chosen?.element ?? null)
  const [merge, setMerge] = useState(chosen?.mergeAdjacent ?? true)
  /** Opened by a control, because asking costs a sign-in over there. */
  const [asking, setAsking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)

  const whose = useLoad(
    useCallback(
      async (signal: AbortSignal) => (asking ? await whoseTimetable(connectionId, signal) : null),
      [connectionId, asking],
    ),
  )

  const person = whose.load.status === 'ready' ? (whose.load.value?.person ?? null) : null
  const choices = whose.load.status === 'ready' ? (whose.load.value?.choices ?? []) : []

  const dirty =
    chosen === null ||
    from !== chosen.from ||
    merge !== chosen.mergeAdjacent ||
    (element?.id ?? null) !== (chosen.element?.id ?? null)

  const save = () => {
    setSaving(true)
    setFailure(null)
    const values: ChosenTimetable = {
      from,
      elementKind: element?.kind ?? null,
      elementId: element?.id ?? null,
      elementName: element?.name ?? '',
      mergeAdjacent: merge,
    }
    chooseTimetable(connectionId, values)
      .then(() => {
        setSaved(new Date().toISOString())
        setSaving(false)
        onSaved()
      })
      .catch((error: unknown) => {
        setFailure(asFailure(error))
        setSaving(false)
      })
  }

  return (
    <>
      {/* ---------------------------------------------------------------------- whose plan */}
      <div className="source__part">
        <h5 className="source__part-title">Whose plan</h5>

        {element !== null ? (
          <p className="source__chosen">
            <strong>{element.name}</strong>
            <Button
              tone="bare"
              onClick={() => {
                setElement(null)
                setAsking(true)
              }}
            >
              Choose a different class
            </Button>
          </p>
        ) : (
          <p className="source__aside">
            Nothing chosen, so MyLib fetches the plan of whoever these credentials are. At a shared
            university login that is nobody in particular — then a class has to be chosen here.
          </p>
        )}

        {!asking && (
          <div className="controls controls--tight">
            <Button
              tone="quiet"
              onClick={() => {
                setAsking(true)
              }}
            >
              Show the classes at {systemName}
            </Button>
          </div>
        )}

        {asking && (
          <>
            {whose.load.status === 'waiting' && (
              <Waiting label={`Asking ${systemName} whose plan it can give.`} />
            )}
            {whose.load.status === 'failed' && (
              <Failed
                failure={whose.load.failure}
                onRetry={whose.retry}
                title={`${systemName} did not answer`}
              />
            )}

            {whose.load.status === 'ready' && person !== null && (
              <p className="source__aside">
                This account is <strong>{person.name}</strong>, so their own plan is what comes
                over. There is nothing to choose.
              </p>
            )}

            {whose.load.status === 'ready' && person === null && (
              <ClassPicker
                choices={choices}
                chosen={element}
                onChoose={(one) => {
                  setElement(one)
                  setAsking(false)
                }}
              />
            )}
          </>
        )}
      </div>

      {/* --------------------------------------------------------------------- from when */}
      <div className="source__part">
        <h5 className="source__part-title">From which day</h5>

        <div className="field field--narrow">
          <label className="field__label" htmlFor={`${base}-from`}>
            Take over everything from
          </label>
          {/* The browser's own field: a picker on a phone, a typed value on a Mac, and understood
              by a screen reader without anything being explained to it. */}
          <input
            id={`${base}-from`}
            className="field__control"
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value)
            }}
          />
        </div>
        <p className="source__aside">
          Everything from that day on comes over, as far ahead as {systemName} publishes it. There
          is no end to set: how far the plan reaches is asked afresh on every sync.
        </p>
      </div>

      {/* ------------------------------------------------------------------------ merging */}
      <div className="source__part">
        <h5 className="source__part-title">Double periods</h5>
        <Switch
          label="Merge double periods into one appointment"
          hint={`${systemName} sends a double period as one entry per period. Merged, two lessons that touch become a single appointment in your timetable.`}
          on={merge}
          onChange={setMerge}
        />
        {chosen !== null && merge !== chosen.mergeAdjacent && (
          <p className="source__warn">
            Changing this makes new appointments once. The ones already taken over stay standing,
            marked as no longer offered, and everything you wrote or linked at them stays with them.
          </p>
        )}
      </div>

      {failure !== null && <Failed failure={failure} />}

      <div className="controls">
        <Button tone="primary" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save these settings'}
        </Button>
        {saved !== null && <Done key={saved} label="Saved" />}
      </div>
    </>
  )
}
